import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
	static targets = [
		'map',
		'jsonMapDataScriptTag',
		'overviewTab',
		'stagePointTab',
		'tabButton',
		'dropdownElement',
		'breadcrumbs',
		'stagePointBackButton',
		'marker',
		'stageLink',
	];

	/**
	 * Connect.
	 */
	connect() {
		// Dev Token
		mapboxgl.accessToken = 'pk.eyJ1Ijoic3dlbnN1cGVyaW50ZXJhY3RpdmUiLCJhIjoiY2tpNXJjaDFiNGdoYjJwa3p3em4zb2JqaiJ9.DWtDr87o2w3RwxOoQH_-Ag';

		// Production Token
		// mapboxgl.accessToken = 'pk.eyJ1Ijoic3dlbnN1cGVyaW50ZXJhY3RpdmUiLCJhIjoiY2wzYmZlZGs2MGV4ZjNkbnJ1eHZqcHA5ZyJ9.GpAxUf8xHR5sXioSFjGHaQ';
	}

	/**
	 * Init the MapBox map.
	 */
	initMap() {
		// Load mapsettings
		this.mapSettings = this.getMapSettings();

		// Init mapbox
		this.map = new mapboxgl.Map({
			container: 'map', // container ID
			style: 'mapbox://styles/swensuperinteractive/cl3bf2is2000t15loa8cb071a', // style URL
			center: [5.28800019752873, 52.175784866316846], // center coordinates - start in center of Netherlands
			zoom: 6.5, // starting zoom
		});

		// Init map correctly zoomed in
		if (this.mapSettings.type == 'stage') {
			this.zoomMapToRouteBounds(this.getGeoJsonRoute(this.mapSettings.postId));
		}

		if (this.mapSettings.type == 'route') {
			this.zoomMapToRouteBounds(this.getGeoJsonRoute('full'));
		}

		// Add zoom and compass controlls
		this.map.addControl(new mapboxgl.NavigationControl());

		// Add support for fullscreening the map
		this.map.addControl(new mapboxgl.FullscreenControl());

		document.addEventListener('fullscreenchange', () => {
			this.map.resize();

			if (this.mapSettings.type == 'stage') {
				this.zoomMapToRouteBounds(this.getGeoJsonRoute(this.mapSettings.postId));
			}

			if (this.mapSettings.type == 'route') {
				this.zoomMapToRouteBounds(this.getGeoJsonRoute('full'));
			}
		});

		// Add routes to map
		this.map.on('load', () => {
			Object.keys(this.mapSettings.routes).forEach((stageId) => {
				if (stageId != 'full') {
					this.addMapRoute(stageId);
				}
			});

			if (this.mapSettings.type == 'stage') {
				this.addMapRouteChangesLines(this.mapSettings.postId);
			}

			Object.keys(this.mapSettings.markers).forEach((stageId) => {
				if (this.mapSettings.type == 'route' || this.mapSettings.postId == stageId) {
					this.addMapMarker(stageId);
				}
			});
		});

		// Open 'routewijzigingen' if hash is present
		if (window.location.hash == '#routewijzigingen') {
			this.tabButtonTargets.forEach((tabButton) => {
				if (tabButton.dataset.tab == 'routewijzigingen') {
					tabButton.click();
				}
			});
		}
	}

	/**
	 * Get GeoJson route.
	 *
	 * @param {Integer} stageId
	 *
	 * @returns JSON
	 */
	getGeoJsonRoute(stageId) {
		return {
			type: 'FeatureCollection',
			features: [
				{
					type: 'Feature',
					properties: {},
					geometry: {
						type: 'LineString',
						coordinates: this.mapSettings.routes[stageId],
					},
				},
			],
		};
	}

	/**
	 * Add route to map.
	 *
	 * @param {Integer} stageId
	 */
	addMapRoute(stageId) {
		var isCurrentStage = this.mapSettings.type == 'stage' && this.mapSettings.postId == stageId;
		var lineColor = '#005130';
		var lineOpacity = 0.5;
		var lineWidth = 2;

		const geoJsonRoute = this.getGeoJsonRoute(stageId);

		this.map.addSource('stage-' + stageId, {
			type: 'geojson',
			data: geoJsonRoute,
		});

		if (isCurrentStage) {
			lineColor = '#006F44';
			lineOpacity = 1;
			lineWidth = 4;
		} else if (this.mapSettings.type == 'route') {
			lineWidth = 4;
		}

		this.map.addLayer({
			id: 'stage-' + stageId,
			type: 'line',
			source: 'stage-' + stageId,
			layout: {
				'line-join': 'round',
				'line-cap': 'round',
			},
			paint: {
				'line-color': lineColor,
				'line-opacity': lineOpacity,
				'line-width': lineWidth,
			},
		});

		if (this.mapSettings.type == 'route') {
			this.initStageLineInteractionEvents(stageId);
		}
	}

	/**
	 * Add map marker for given stage.
	 *
	 * @param {Integer} stageId
	 */
	addMapMarker(stageId) {
		const geoJson = this.getGeoJsonMapMarkers(stageId);

		geoJson.features.forEach((marker, index) => {
			// Only display these marker types on route overview page.
			if (this.mapSettings.type === 'route') {
				if (
					![
						'routewijzigingen',
						'ovtreinstations',
						'ovoverigeopstappunten',
						'veerponten',
						'overnachtingslocaties',
						'parkeerplaatsen',
						'restrictieshonden',
						'waarschuwingen',
					].includes(marker.properties.type)
				) {
					return;
				}
			}

			// Create a DOM element for each marker.
			const markerElement = document.createElement('div');

			markerElement.className = 'route-marker';
			markerElement.style.backgroundImage = 'url("' + marker.properties.iconUrl + '")';
			markerElement.setAttribute('data-point-id', marker.properties.id);
			markerElement.setAttribute('data-marker-type', marker.properties.type);
			markerElement.setAttribute('data-longitude', marker.geometry.coordinates[0]);
			markerElement.setAttribute('data-latitude', marker.geometry.coordinates[1]);
			markerElement.setAttribute('data-route-target', 'marker');
			markerElement.setAttribute('data-has-gpx-line', marker.properties.hasGpxLine);

			if (this.mapSettings.type === 'route') {
				markerElement.style.cursor = 'grab';
			}

			// Only allow click action on stage pages.
			if (this.mapSettings.type === 'stage') {
				markerElement.setAttribute('data-action', 'mouseover->route#hoverMarker mouseleave->route#unHoverMarker click->route#clickMarker');
			}

			// Add markers to the map.
			new mapboxgl.Marker(markerElement).setLngLat(marker.geometry.coordinates).addTo(this.map);
		});
	}

	/**
	 * Add route change to map.
	 *
	 * @param {Integer} stageId
	 */
	addMapRouteChangesLines(stageId) {
		if (!this.mapSettings.markers[stageId] || this.mapSettings.markers[stageId] == 'undefined') {
			return;
		}

		Object.keys(this.mapSettings.markers[stageId]).forEach((markerCategory) => {
			this.mapSettings.markers[stageId][markerCategory].forEach((currentMarker) => {
				if (!currentMarker.gpxFileArray) {
					return;
				}

				var lineColor = '#E32517';
				var lineOpacity = 1;
				var lineWidth = 4;

				const geoJsonRoute = {
					type: 'FeatureCollection',
					features: [
						{
							type: 'Feature',
							properties: {},
							geometry: {
								type: 'LineString',
								coordinates: JSON.parse(currentMarker.gpxFileArray),
							},
						},
					],
				};

				this.map.addSource('line-' + currentMarker.id, {
					type: 'geojson',
					data: geoJsonRoute,
				});

				this.map.addLayer({
					id: 'line-' + currentMarker.id,
					type: 'line',
					source: 'line-' + currentMarker.id,
					layout: {
						'line-join': 'round',
						'line-cap': 'round',
					},
					paint: {
						'line-color': lineColor,
						'line-opacity': lineOpacity,
						'line-width': lineWidth,
					},
				});
			});
		});

		// if (this.mapSettings.type == 'route') {
		// 	this.initStageLineInteractionEvents(stageId);
		// }
	}

	/**
	 * Get GeoJson for map markers.
	 *
	 * @param {Integer} stageId
	 *
	 * @returns JSON
	 */
	getGeoJsonMapMarkers(stageId) {
		const geoJsonMapMarkers = {
			type: 'FeatureCollection',
			features: [],
		};

		Object.keys(this.mapSettings.markers[stageId]).forEach((markerCategory) => {
			this.mapSettings.markers[stageId][markerCategory].forEach((currentMarker) => {
				geoJsonMapMarkers.features.push({
					type: 'Feature',
					properties: {
						id: currentMarker.id,
						iconUrl: currentMarker.iconUrl,
						type: currentMarker.type,
						hasGpxLine: currentMarker.gpxFileArray,
					},
					geometry: {
						type: 'Point',
						coordinates: [currentMarker.coordinates.longitude, currentMarker.coordinates.latitude],
					},
				});
			});
		});

		return geoJsonMapMarkers;
	}

	/**
	 * Init stage line interaction events
	 *
	 * @param {Integer} stageId
	 */
	initStageLineInteractionEvents(stageId) {
		const stageLink = document.querySelector('[data-stage-id="stage-' + stageId + '"]');

		this.map.on('mouseenter', 'stage-' + stageId, (e) => {
			this.map.setPaintProperty('stage-' + stageId, 'line-color', '#006F44');
			this.map.setPaintProperty('stage-' + stageId, 'line-opacity', 1);
			this.map.getCanvas().style.cursor = 'pointer';

			stageLink.setAttribute('data-hover', true);
			stageLink.setAttribute('data-group-hover', true);
		});

		this.map.on('mouseleave', 'stage-' + stageId, (e) => {
			this.map.setPaintProperty('stage-' + stageId, 'line-color', '#005130');
			this.map.setPaintProperty('stage-' + stageId, 'line-opacity', 0.5);
			this.map.getCanvas().style.cursor = '';
			stageLink.removeAttribute('data-hover');
			stageLink.removeAttribute('data-group-hover');
		});

		this.map.on('click', 'stage-' + stageId, (e) => {
			stageLink.click();
		});
	}

	/**
	 * Zoom map to round bounds.
	 *
	 * @param {JSON} geoJsonRoute
	 */
	zoomMapToRouteBounds(geoJsonRoute, duration = 0, padding = 60) {
		// Geographic coordinates of the LineString
		const coordinates = geoJsonRoute.features[0].geometry.coordinates;

		// Create a 'LngLatBounds' with both corners at the first coordinate.
		const bounds = new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]);

		// Extend the 'LngLatBounds' to include every coordinate in the bounds result.
		for (const coord of coordinates) {
			bounds.extend(coord);
		}

		// Set padding on zoomed map
		this.map.fitBounds(bounds, {
			padding: padding,
			duration: duration,
			linear: false,
		});
	}

	/**
	 * Get dropdown element from pointId string.
	 *
	 * @param {String} pointId
	 */
	getDropdownElement(pointId) {
		var returnedElement = false;

		this.dropdownElementTargets.forEach((dropdownElement) => {
			if (dropdownElement.dataset.pointId == pointId) {
				returnedElement = dropdownElement;
			}
		});

		return returnedElement;
	}

	/**
	 * Click marker.
	 *
	 * @param {Event} e
	 */
	clickMarker(e) {
		e.preventDefault();

		const matchedDropdownElement = this.getDropdownElement(e.currentTarget.dataset.pointId);

		if (matchedDropdownElement) {
			matchedDropdownElement.querySelector('[data-action]').click();
		}

		// Switch to tab if not yet open
		const markerType = e.currentTarget.dataset.markerType;

		this.stagePointTabTargets.forEach((element) => {
			if (markerType == element.dataset.tab) {
				element.classList.remove('hidden');
			} else {
				element.classList.add('hidden');
			}
		});

		this.breadcrumbsTargets.forEach(function (breadCrumbsTarget) {
			breadCrumbsTarget.classList.add('hidden');
		});

		this.stagePointBackButtonTargets.forEach(function (stagePointBackButton) {
			stagePointBackButton.classList.remove('hidden');
		});

		if (!this.overviewTabTarget.classList.contains('hidden')) {
			this.overviewTabTarget.classList.add('hidden');
		}

		this.removeHashFromUrl();
	}

	/**
	 * Hover marker.
	 *
	 * @param {Event} e
	 */
	hoverMarker(e) {
		e.preventDefault();

		const matchedDropdownElement = this.getDropdownElement(e.currentTarget.dataset.pointId);

		if (matchedDropdownElement) {
			matchedDropdownElement.setAttribute('data-group-hover', true);
		}
	}

	/**
	 * Un-hover marker.
	 *
	 * @param {Event} e
	 */
	unHoverMarker(e) {
		e.preventDefault();

		const matchedDropdownElement = this.getDropdownElement(e.currentTarget.dataset.pointId);

		if (matchedDropdownElement) {
			matchedDropdownElement.removeAttribute('data-group-hover');
		}
	}

	/**
	 * Get map settings (from jsonMapDataScriptTagTarget)
	 *
	 * @returns Object
	 */
	getMapSettings() {
		return JSON.parse(this.jsonMapDataScriptTagTarget.innerHTML);
	}

	/**
	 * Open stage point tab.
	 *
	 * @param {Event} e
	 */
	openStagePointTab(e) {
		e.preventDefault();

		const tab = e.currentTarget.dataset.tab;

		this.stagePointTabTargets.forEach((element) => {
			if (tab == element.dataset.tab) {
				element.classList.remove('hidden');
			} else {
				element.classList.add('hidden');
			}
		});

		this.breadcrumbsTargets.forEach(function (breadCrumbsTarget) {
			breadCrumbsTarget.classList.add('hidden');
		});

		this.stagePointBackButtonTargets.forEach(function (stagePointBackButton) {
			stagePointBackButton.classList.remove('hidden');
		});

		if (!this.overviewTabTarget.classList.contains('hidden')) {
			this.overviewTabTarget.classList.add('hidden');
		}

		if (window.innerWidth > 1024) {
			window.scrollTo(0, 208);
		}
	}

	/**
	 * Close stage point tab.
	 *
	 * @param {Event} e
	 */
	closeStagePointTab(e) {
		e.preventDefault();

		this.overviewTabTarget.classList.remove('hidden');

		this.breadcrumbsTargets.forEach(function (breadCrumbsTarget) {
			breadCrumbsTarget.classList.remove('hidden');
		});

		this.stagePointBackButtonTargets.forEach(function (stagePointBackButton) {
			stagePointBackButton.classList.add('hidden');
		});

		this.stagePointTabTargets.forEach((element) => {
			element.classList.add('hidden');
		});

		this.dropdownElementTargets.forEach((dropdownElement) => {
			dropdownElement.removeAttribute('data-group-active-sub');
		});

		this.zoomMapToRouteBounds(this.getGeoJsonRoute(this.mapSettings.postId), 500);

		this.markerTargets.forEach((marker) => {
			marker.style.opacity = '1';
		});

		this.removeHashFromUrl();
	}

	/**
	 * Toggle stage point category item.
	 *
	 * @param {Event} e
	 */
	toggleStagePointCategoryItem(e) {
		e.preventDefault();

		const parent = e.currentTarget.closest('.group-active-sub');

		// Close all open dropdown elements, except the clicked on.
		this.dropdownElementTargets.forEach((dropdownElement) => {
			if (dropdownElement !== parent) {
				dropdownElement.removeAttribute('data-group-active-sub');
			}
		});

		// Check if current dropdown element is open, if so, close it and zoom back to default state.
		if (parent.hasAttribute('data-group-active-sub')) {
			parent.removeAttribute('data-group-active-sub');
			this.zoomMapToRouteBounds(this.getGeoJsonRoute(this.mapSettings.postId), 500);

			this.markerTargets.forEach((marker) => {
				marker.style.opacity = '1';
			});

			// If current dropdown element is closed, open it.
		} else {
			this.markerTargets.forEach((marker) => {
				if (marker.dataset.pointId === parent.dataset.pointId) {
					if (marker.getAttribute('data-has-gpx-line') !== '' && marker.dataset.hasGpxLine !== null && marker.dataset.hasGpxLine !== 'undefined') {
						this.zoomMapToRouteBounds(this.map.getSource('line-' + marker.dataset.pointId)._data, 1500);
					} else {
						this.map.flyTo({
							center: [parseFloat(marker.dataset.longitude), parseFloat(marker.dataset.latitude)],
							zoom: 16,
						});
					}

					marker.style.opacity = '1';
				} else {
					marker.style.opacity = '0.5';
				}
			});

			parent.setAttribute('data-group-active-sub', true);
		}
	}

	/**
	 * Hover stage link.
	 *
	 * @param {Event} e
	 */
	hoverStageLink(e) {
		e.preventDefault();

		const stageId = e.currentTarget.dataset.stageId;

		this.map.setPaintProperty(stageId, 'line-color', '#006F44');
		this.map.setPaintProperty(stageId, 'line-opacity', 1);
	}

	/**
	 * Unhover stage link.
	 *
	 * @param {Event} e
	 */
	unHoverStageLink(e) {
		e.preventDefault();

		const stageId = e.currentTarget.dataset.stageId;

		this.map.setPaintProperty(stageId, 'line-color', '#005130');
		this.map.setPaintProperty(stageId, 'line-opacity', 0.5);
	}

	/**
	 * Remove hash from URL.
	 */
	removeHashFromUrl() {
		if (window.location.hash == '#routewijzigingen') {
			history.replaceState('', document.title, window.location.pathname + window.location.search);
		}
	}
}
